import { c as _c } from "react/compiler-runtime";
import type { L10n } from '@onefootprint/core';
import { Idv2, Idv2Provider, LoadNeuroId } from '@onefootprint/idv2';
import { getErrorMessage } from '@onefootprint/request';
import type { IdDocOutcome, OverallOutcome } from '@onefootprint/types';
import { SessionStatus } from '@onefootprint/types';
import { type ComponentProps, useEffect, useMemo } from 'react';
import { type DoneArgs, Identify } from '@/idv/components/identify';
import { IdentifyVariant } from '@/idv/components/identify/identify.types';
import GenericErrorPage from '../../../src/components/gerenic-error-page/generic-error-page';
import { IDV_SESSION_RETRY_LIMIT } from '../../../src/config/constants';
import { AppErrorBoundary, SessionExpired, useLayoutOptions } from '../../components';
import { L10nContextProvider } from '../../components/l10n-provider';
import { useIdvMachine, useLogStateMachine } from '../../hooks';
import { useValidateSession } from '../../queries';
import { convertBootstrapToVaultData, FPCustomEvents, getLogger, trackAction } from '../../utils';
import type { IdvMachineArgs } from '../../utils/state-machine/machine';
import { createAuthTokenChangedPayload, createReceivedDeviceResponseJsonPayload } from '../../utils/state-machine/utils/custom-listener';
import ConfigInvalid from '../config-invalid';
import Init from '../init';
import Onboarding from '../onboarding';
type RouterProps = {
  l10n?: L10n;
  onIdentifyDone?: ({
    authToken
  }: {
    authToken: string;
  }) => void;
};
const {
  receivedDeviceResponseJson,
  stepUpCompleted
} = FPCustomEvents;
const {
  logInfo,
  logWarn
} = getLogger({
  location: 'idv-router'
});

// Internal orgs, the dev E2E org among them, identify on idv2 whatever UseIdentifyV2 serves, so e2e
// and backends running without LaunchDarkly keep covering it.
const IDENTIFY_V2_ORG_IDS = new Set(['org_hyZP3ksCvsT0AlLqMZsgrI', 'org_EA6MoHnwQDQqNdriEjgWon', 'org_wrMg7lhSpZif14SIhC9ihL']);
type Idv2SessionArgs = Pick<IdvMachineArgs, 'authToken' | 'obConfigAuth'> & {
  identifiesOnIdv2: boolean;
  isSandboxOutcome: boolean;
};

// idv2 creates the identify session itself, from the tenant's auth token when there is one.
const getIdv2Session = ({
  identifiesOnIdv2,
  isSandboxOutcome,
  authToken,
  obConfigAuth
}: Idv2SessionArgs) => identifiesOnIdv2 ? {
  onboardingSessionToken: '',
  initialAuthToken: authToken,
  obConfigAuth
} : {
  onboardingSessionToken: authToken ?? '',
  obConfigAuth: isSandboxOutcome ? obConfigAuth : undefined
};

/**
 * Renders inside Idv2Provider.
 * During sandbox-outcome: renders <Idv2 initialPath="/sandbox-outcome">.
 * During identify on idv2: renders <Idv2 initialPath="/identify">, which hands the auth token back
 * so the machine moves on to onboarding.
 * During onboarding: renders <Onboarding>. The inner router
 * (onboarding/pages/router/router.tsx) handles relayToIDV2 and renders
 * <Idv2> as a child when true — keeping xstate alive across relay toggles.
 */
const Idv2OrLegacy = t0 => {
  const $ = _c(5);
  const {
    isSandboxOutcome,
    identifiesOnIdv2,
    onIdentifyDone,
    onboardingProps
  } = t0;
  if (isSandboxOutcome) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Idv2 initialPath="/sandbox-outcome" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (identifiesOnIdv2) {
    let t1;
    if ($[1] !== onIdentifyDone) {
      t1 = <Idv2 initialPath="/identify" stopAfterIdentify={true} onIdentifyDone={onIdentifyDone} />;
      $[1] = onIdentifyDone;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== onboardingProps) {
    t1 = <Onboarding {...onboardingProps} />;
    $[3] = onboardingProps;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const Router = t0 => {
  const $ = _c(34);
  const {
    l10n,
    onIdentifyDone
  } = t0;
  const [state, send, service] = useIdvMachine();
  useLogStateMachine("idv", state);
  const {
    options: layoutOptions
  } = useLayoutOptions();
  const {
    config,
    device,
    authToken,
    bootstrapData,
    isTransfer,
    componentsSdkContext,
    isInIframe,
    obConfigAuth,
    idDocOutcome,
    overallOutcome,
    onClose,
    onComplete,
    autoAdvance,
    onBackButtonVisibilityChange,
    onRequirementChange,
    onExternalBack,
    retries,
    showLogo,
    sandboxId,
    fixtureRiskSignals
  } = state.context;
  const isIdv2Ready = !!config && !!device;
  const retryLimitExceeded = retries > IDV_SESSION_RETRY_LIMIT;
  const isIdentifying = state.matches("identify");
  const identifiesOnIdv2 = isIdentifying && !!config && (IDENTIFY_V2_ORG_IDS.has(config.orgId) || !!config.flags.useIdentifyV2);
  const identifiesOnLegacy = isIdentifying && !identifiesOnIdv2;
  useEffect(() => {
    if (identifiesOnLegacy) {
      logInfo("Identify started", {
        engine: "idv"
      });
    }
  }, [identifiesOnLegacy]);
  let t1;
  if ($[0] !== bootstrapData) {
    t1 = convertBootstrapToVaultData(bootstrapData);
    $[0] = bootstrapData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    kycData: kycBootstrapData,
    kybData: kybBootstrapData
  } = t1;
  let t2;
  if ($[2] !== authToken) {
    t2 = {
      authToken
    };
    $[2] = authToken;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== send) {
    t3 = {
      onSuccess: sessionStatus => {
        if (sessionStatus !== SessionStatus.active) {
          send({
            type: "expireSession"
          });
          trackAction("expired:check_session", {
            sessionStatus
          });
          logWarn(`Session status: ${sessionStatus}`);
        }
      },
      onError: _temp
    };
    $[4] = send;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useValidateSession(t2, t3);
  let t4;
  if ($[6] !== send) {
    t4 = () => {
      const authTokenListener = e => {
        const payload = createAuthTokenChangedPayload(e);
        if (payload) {
          send(payload);
        }
      };
      const deviceResponseJsonListener = e_0 => {
        const payload_0 = createReceivedDeviceResponseJsonPayload(e_0);
        if (payload_0) {
          send(payload_0);
        }
      };
      document.addEventListener(stepUpCompleted, authTokenListener);
      document.addEventListener(receivedDeviceResponseJson, deviceResponseJsonListener);
      return function cleanup() {
        document.removeEventListener(stepUpCompleted, authTokenListener);
        document.removeEventListener(receivedDeviceResponseJson, deviceResponseJsonListener);
      };
    };
    $[6] = send;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  const handleIdentifyDone = payload_1 => {
    send({
      type: "identifyCompleted",
      payload: payload_1
    });
    if (onIdentifyDone && payload_1) {
      onIdentifyDone(payload_1);
    }
    trackAction("identify:completed");
    if (!identifiesOnIdv2) {
      logInfo("Identify completed", {
        engine: "idv"
      });
    }
  };
  const T0 = AppErrorBoundary;
  let t6;
  if ($[9] !== send) {
    t6 = () => send({
      type: "reset"
    });
    $[9] = send;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== config || $[12] !== isTransfer) {
    t7 = !isTransfer && <LoadNeuroId config={config} />;
    $[11] = config;
    $[12] = isTransfer;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== state) {
    t8 = state.matches("init") ? <Init /> : null;
    $[14] = state;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = identifiesOnLegacy && config && device ? <L10nContextProvider l10n={l10n}><Identify initArgs={{
      variant: IdentifyVariant.verify,
      device,
      config,
      isLive: config.isLive,
      overallOutcome,
      sandboxId,
      initialAuthToken: authToken,
      obConfigAuth,
      isComponentsSdk: !!componentsSdkContext,
      bootstrapData,
      logoConfig: showLogo ? {
        orgName: config.orgName,
        logoUrl: config.logoUrl || undefined
      } : undefined
    }} onDone={handleIdentifyDone} onBackButtonVisibilityChange={onBackButtonVisibilityChange} onExternalBack={onExternalBack} /></L10nContextProvider> : null;
  const t10 = (state.matches("sandboxOutcome") || identifiesOnIdv2 || state.matches("onboarding")) && isIdv2Ready ? <Idv2Provider key={identifiesOnIdv2 ? "identify" : "flow"} initialValues={{
    ...getIdv2Session({
      identifiesOnIdv2,
      isSandboxOutcome: state.matches("sandboxOutcome"),
      authToken,
      obConfigAuth
    }),
    config,
    overallOutcome,
    fixtureRiskSignals,
    idDocOutcome,
    l10n: {
      locale: l10n?.locale,
      language: l10n?.language ?? "en"
    },
    kycBootstrapData,
    kybBootstrapData,
    isTransfer,
    sandboxId,
    isComponentsSdk: !!componentsSdkContext,
    showLogo
  }} onComplete={(validationToken, options) => {
    if (!options?.hasCompletionScreen) {
      send({
        type: "onboardingCompleted",
        payload: {
          validationToken
        }
      });
    }
    trackAction("onboarding:completed");
    onComplete?.({
      validationToken,
      authToken,
      deviceResponseJson: service.getSnapshot().context.deviceResponseJson,
      hasCompletionScreen: options?.hasCompletionScreen
    });
  }} onHandoffComplete={() => {
    onComplete?.({});
  }} onSandboxOutcomeSubmit={payload_2 => send({
    type: "sandboxOutcomeSubmitted",
    payload: {
      overallOutcome: payload_2.overallOutcome as OverallOutcome | undefined,
      fixtureRiskSignals: payload_2.fixtureRiskSignals,
      idDocOutcome: payload_2.idDocOutcome as IdDocOutcome | undefined,
      sandboxId: payload_2.sandboxId
    }
  })} onClose={onClose} autoAdvance={autoAdvance} onBackButtonVisibilityChange={onBackButtonVisibilityChange} onRequirementChange={onRequirementChange} hideNavigationButtons={layoutOptions?.hideHeaderButton} componentsSdkRelay={componentsSdkContext && !componentsSdkContext.skipRelayToComponents ? {
    relayToComponents: componentsSdkContext.relayToComponents,
    onRelayFromComponents: componentsSdkContext.onRelayFromComponents
  } : undefined}><Idv2OrLegacy isSandboxOutcome={state.matches("sandboxOutcome")} identifiesOnIdv2={identifiesOnIdv2} onIdentifyDone={handleIdentifyDone} onboardingProps={{
      config,
      idvContext: {
        device,
        authToken: authToken ?? "",
        isTransfer,
        componentsSdkContext,
        isInIframe
      },
      overallOutcome,
      fixtureRiskSignals,
      idDocOutcome: idDocOutcome as IdDocOutcome | undefined,
      onClose
    }} /></Idv2Provider> : null;
  let t11;
  if ($[16] !== state) {
    t11 = state.matches("sessionExpired") ? <SessionExpired /> : null;
    $[16] = state;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== retryLimitExceeded || $[19] !== send || $[20] !== state) {
    t12 = state.matches("initConfigFailed") ? <GenericErrorPage onRetry={() => send({
      type: "reset"
    })} retryLimitExceeded={retryLimitExceeded} /> : null;
    $[18] = retryLimitExceeded;
    $[19] = send;
    $[20] = state;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== state) {
    t13 = state.matches("configInvalid") ? <ConfigInvalid /> : null;
    $[22] = state;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== T0 || $[25] !== t10 || $[26] !== t11 || $[27] !== t12 || $[28] !== t13 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t14 = <T0 onReset={t6}>{t7}{t8}{t9}{t10}{t11}{t12}{t13}</T0>;
    $[24] = T0;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
};
export default Router;
function _temp(error) {
  logWarn(`Validating user session failed with error: ${getErrorMessage(error)}`, error);
}